import TenantLayout from "@/pages/tenant/components/TenantLayout";
import { tenantAccount, planLabels } from "@/mocks/tenants";

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysUntil(dateStr: string) {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export default function TenantAccountPage() {
  const plan = planLabels[tenantAccount.plan];
  const expireDays = daysUntil(tenantAccount.expireAt);

  return (
    <TenantLayout>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info card */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">租户账户信息</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 shrink-0">
                  <i className="ri-building-2-line text-orange-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">租户名称</p>
                  <p className="text-sm font-semibold text-gray-800">{tenantAccount.name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 shrink-0">
                  <i className="ri-user-line text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">管理员账号</p>
                  <p className="text-sm font-semibold text-gray-800">{tenantAccount.username}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 shrink-0">
                  <i className="ri-vip-crown-line text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">当前套餐</p>
                  <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${plan.bg} ${plan.color}`}>
                    {plan.label}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-purple-50 shrink-0">
                  <i className="ri-server-line text-purple-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">站点配额</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {tenantAccount.usedSites} / {tenantAccount.maxSites} 个
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 shrink-0">
                  <i className="ri-time-line text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">账户到期时间</p>
                  <p className="text-sm font-semibold text-gray-800">{formatDate(tenantAccount.expireAt)}</p>
                  <p className="text-xs text-red-500 mt-0.5">
                    剩余 {expireDays} 天
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-yellow-50 shrink-0">
                  <i className="ri-wallet-3-line text-yellow-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">账户余额</p>
                  <p className="text-sm font-semibold text-gray-800">¥ {tenantAccount.balance.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <h4 className="text-sm font-medium text-gray-700 mb-3">套餐权益</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { icon: "ri-check-line", text: "无限数据条数" },
                  { icon: "ri-check-line", text: "20个站点" },
                  { icon: "ri-check-line", text: "自定义域名" },
                  { icon: "ri-check-line", text: "CDN加速" },
                  { icon: "ri-check-line", text: "优先技术支持" },
                  { icon: "ri-check-line", text: "API接口访问" },
                  { icon: "ri-check-line", text: "数据统计分析" },
                  { icon: "ri-check-line", text: "批量操作" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <i className={`${item.icon} text-green-500`} />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Side card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">到期提醒</h3>
            <div className="space-y-4">
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-alarm-warning-line text-orange-500" />
                  <span className="text-sm font-medium text-orange-700">账户即将到期</span>
                </div>
                <p className="text-xs text-orange-600 leading-relaxed">
                  您的{plan.label}将于 {formatDate(tenantAccount.expireAt)} 到期，为避免服务中断，请提前续费。
                </p>
              </div>
              <button className="w-full py-2.5 bg-orange-500 text-white text-sm font-medium rounded-xl hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer">
                立即续费
              </button>
            </div>
          </div>
        </div>
      </div>
    </TenantLayout>
  );
}